import React, { type ReactNode } from "react";
import type { BaseProps } from "@purpur/common-types";
import { Icon } from "@purpur/icon";
import { alert } from "@purpur/icon/assets/alert";
import { checkCircle } from "@purpur/icon/assets/check-circle";
import { devices } from "@purpur/icon/assets/devices";
import { error } from "@purpur/icon/assets/error";
import { info } from "@purpur/icon/assets/info";
import { offering } from "@purpur/icon/assets/offering";
import { question } from "@purpur/icon/assets/question";
import { tag } from "@purpur/icon/assets/tag";
import c from "classnames";

import styles from "./badge.module.scss";
const rootClassName = "purpur-badge";

export const BADGE_VARIANT = {
  ATTENTION: "attention",
  BLACK_FRIDAY: "black-friday",
  ERROR: "error",
  GENERAL: "general",
  INFORMATION: "information",
  NEUTRAL: "neutral",
  SPECIAL: "special",
  SUCCESS: "success",
  WARNING: "warning",
} as const;

export const badgeVariants = Object.values(BADGE_VARIANT);
export type BadgeVariant = (typeof BADGE_VARIANT)[keyof typeof BADGE_VARIANT];

const getIcon = (variant: BadgeVariant) => {
  switch (variant) {
    case BADGE_VARIANT.ATTENTION:
    case BADGE_VARIANT.SPECIAL:
      return offering;
    case BADGE_VARIANT.INFORMATION:
      return info;
    case BADGE_VARIANT.GENERAL:
      return devices;
    case BADGE_VARIANT.SUCCESS:
      return checkCircle;
    case BADGE_VARIANT.WARNING:
      return alert;
    case BADGE_VARIANT.ERROR:
      return error;
    case BADGE_VARIANT.NEUTRAL:
      return question;
    case BADGE_VARIANT.BLACK_FRIDAY:
      return tag;
    default:
      return;
  }
};

type WithIcon = {
  showIcon?: true;
  iconAllyTitle?: string;
};

type WithoutIcon = {
  showIcon: false;
  iconAllyTitle?: never;
};

export type BadgeProps = Omit<BaseProps<"span">, "children"> & {
  children: ReactNode;
  variant: BadgeVariant;
} & (WithIcon | WithoutIcon);

export const Badge = ({
  children,
  showIcon = true,
  iconAllyTitle,
  variant,
  ["data-testid"]: dataTestid,
  className = "",
  ...props
}: BadgeProps) => {
  const icon = getIcon(variant);

  const classes = c([className, styles[rootClassName], styles[`${rootClassName}--${variant}`]]);

  return (
    <span className={classes} data-testid={dataTestid} {...props}>
      {showIcon && icon && (
        <Icon
          data-testid={`${dataTestid}-icon`}
          allyTitle={iconAllyTitle}
          size="xs"
          svg={icon}
          className={c(styles[`${rootClassName}__icon`])}
        />
      )}
      {children}
    </span>
  );
};
